import React, { useLayoutEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useConfig } from "../../hooks/useConfig"
import EditTabs from "./EditTabs"
import BackgroundToolbar from "./BackgroundToolbar"
import StickersToolbar from "./StickersToolbar"
import OverlaysToolbar from "./OverlaysToolbar"
import AiEffectToolbar from "./AiEffectToolbar"
import { CSSTransition, SwitchTransition } from "react-transition-group"
import * as StickerUtil from "../../utils/StickerUtil"
import Box from "@mui/material/Box"
import { Button, Divider, Stack } from "@mui/material"
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined"
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined"
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined"
import SendOutlinedIcon from "@mui/icons-material/SendOutlined"

interface EditToolbarsProps {
  tools: string[]
  rawImage: string
  userImage: string
  onChangeBackground: (url: string) => void
  onChangeOverlay: (url: string | null) => void
  onBack?: () => void
  onRetake: () => void
  onSubmit: () => void
  aiEffectRemainingRegenerations?: number
  aiEffectIsProcessing?: boolean
  onAiEffectRegenerate?: () => void
  aiImageHistory?: string[]
  selectedAiImageIndex?: number
  onAiImageSelect?: (index: number) => void
}

export default function EditControls({
  tools,
  rawImage,
  userImage,
  onChangeBackground,
  onChangeOverlay,
  onBack,
  onRetake,
  onSubmit,
  aiEffectRemainingRegenerations,
  aiEffectIsProcessing,
  onAiEffectRegenerate,
  aiImageHistory,
  selectedAiImageIndex,
  onAiImageSelect,
}: EditToolbarsProps) {
  const { t } = useTranslation()
  const config = useConfig()
  const [selectedToolIndex, setSelectedToolIndex] = useState(0)
  const [selectedBackgroundIndex, setSelectedBackgroundIndex] = useState(0)
  const [selectedOverlayIndex, setSelectedOverlayIndex] = useState(0)

  useLayoutEffect(() => {
    onChangeBackground(backgroundUrls[0])
    onChangeOverlay(config.overlays[0]?.url ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const theme = config.theme
  const buttonSx = {
    color: theme.textColor,
    px: 1.5,
    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.15)" },
  }
  const bgUrls = config.backgrounds.map((b) => b.url)
  const backgroundUrls = config.backgroundReplacementRequired ? bgUrls : [rawImage, ...bgUrls]

  const selectTool = (newIndex: number) => {
    if (newIndex < tools.length && newIndex !== selectedToolIndex) setSelectedToolIndex(newIndex)
  }

  const selectBackground = (newIndex: number) => {
    if (newIndex < backgroundUrls.length && newIndex !== selectedBackgroundIndex) {
      setSelectedBackgroundIndex(newIndex)
      onChangeBackground(backgroundUrls[newIndex])
    }
  }

  const selectOverlay = (newIndex: number) => {
    if (newIndex < config.overlays.length && newIndex !== selectedOverlayIndex) {
      setSelectedOverlayIndex(newIndex)
      onChangeOverlay(config.overlays[newIndex]?.url)
    }
  }

  const currentTool = tools[selectedToolIndex]

  return (
    <Box
      id="editToolsContainer"
      sx={{ width: 1, maxWidth: 720 }}
    >
      {tools.length > 1 && (
        <EditTabs
          theme={theme}
          tools={tools}
          selectedIndex={selectedToolIndex}
          onSelect={selectTool}
        />
      )}

      <Box
        sx={{
          pb: 2,
          bgcolor: "rgba(255, 255, 255, 0.8)",
          borderRadius: tools.length <= 1 ? 2 : "0 0 8px 8px",
        }}
      >
        <SwitchTransition>
          <CSSTransition
            key={currentTool}
            timeout={200}
            classNames="shortFade"
          >
            <>
              {currentTool === "backgrounds" && (
                <BackgroundToolbar
                  backgroundUrls={backgroundUrls}
                  selectedIndex={selectedBackgroundIndex}
                  handleSelect={selectBackground}
                />
              )}

              {currentTool === "stickers" && (
                <StickersToolbar
                  stickers={config.stickers}
                  handleSticker={StickerUtil.addSticker}
                />
              )}

              {currentTool === "overlays" && (
                <OverlaysToolbar
                  background={userImage ?? ""}
                  overlays={config.overlays}
                  handleSelect={(i: number) => selectOverlay(i)}
                />
              )}

              {currentTool === "ai_effect" && onAiEffectRegenerate && (
                <AiEffectToolbar
                  remainingRegenerations={aiEffectRemainingRegenerations ?? 0}
                  isProcessing={aiEffectIsProcessing ?? false}
                  onRegenerate={onAiEffectRegenerate}
                  imageUrls={aiImageHistory ?? []}
                  selectedIndex={selectedAiImageIndex ?? 0}
                  onSelect={onAiImageSelect ?? (() => {})}
                />
              )}
            </>
          </CSSTransition>
        </SwitchTransition>

        <Divider sx={{ mt: 2 }} />

        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          sx={{ mt: 3, px: 2 }}
        >
          {onBack ? (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={onBack}
            >
              {t("common.back")}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ReplayOutlinedIcon />}
              onClick={onRetake}
            >
              {t("capture.retake")}
            </Button>
          )}

          {tools.length > 0 && config.showGalleryOptIn ? (
            <Button
              variant="contained"
              startIcon={<DoneOutlinedIcon />}
              onClick={onSubmit}
            >
              {t("edit.done")}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<SendOutlinedIcon />}
              onClick={onSubmit}
            >
              {t("capture.post")}
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  )
}
